import { ClipboardEvent, KeyboardEvent, useEffect, useMemo, useState } from "react";

import { Select } from "antd";
import styled from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import FieldErrorMessage from "components/base/FieldErrorMessage";
import { WELL_CARD_VIEW_SOURCE } from "components/well-card-panel/constants";

import { JUMP_MAX_HIGHLIGHT_WELLS, collectWellEntries } from "../utils/jumpToWells";
import { JumpActionRow, jumpPanelStyles } from "./jumpPanelLayout";
import { WellJumpFormat, useFilterWellOptions } from "./useFilterWellOptions";
import { formatMatchedCount, useWellJump } from "./useWellJump";

const ENTRY_CAP_MESSAGE = `Only the first ${JUMP_MAX_HIGHLIGHT_WELLS} entries were used`;
const WELL_NAMES_FAILED_MESSAGE = "Could not load well names";

/** How long after the last keystroke the option list is re-filtered. */
const SEARCH_DEBOUNCE_MS = 150;

/**
 * Ceiling on rendered options. rc-select virtualises the dropdown, so the DOM is
 * not the cost — the per-keystroke scan and the option objects are.
 */
const MAX_RENDERED_OPTIONS = 200;

const FORMAT_OPTIONS: { value: WellJumpFormat; label: string }[] = [
  { value: "entityName", label: "Entity Name" },
  { value: "wellName", label: "Well Name" },
  { value: "uwid", label: "Unformatted UWID" },
  { value: "api10", label: "API10" },
  { value: "mosaic", label: "Mosaic" }
];

interface WellJumpSelectorProps {
  closeMenu: () => void;
  /**
   * Whether the Jump to Location panel is open. This component stays mounted
   * (antd's Tabs keep inactive panes alive), so without it a response would land
   * and fly the camera after the user closed the panel.
   */
  isOpen?: boolean;
}

export default function WellJumpSelector({
  closeMenu,
  isOpen = true
}: WellJumpSelectorProps) {
  // One list: dropdown entries are already unformatted UWIs, pasted ones are
  // normalised on the way in, and nothing downstream tells them apart.
  const [selected, setSelected] = useState<string[]>([]);
  const [format, setFormat] = useState<WellJumpFormat>("entityName");

  // Shared with the Ctrl+A quick-entry modal — see useWellJump.
  const { status, error, matched, submitJump, clearJump, cancelPending, resetFeedback } =
    useWellJump(WELL_CARD_VIEW_SOURCE.jumpToLocation);

  // One-way latch on the WELL DROPDOWN, not on the panel: Well is the first and
  // default tab, so latching on panel-open made every Jump to Location — even
  // one that only wanted Lat/Lon — pull 50,000 rows for a dropdown nobody
  // opened. See useFilterWellOptions for why it must never flip back.
  const [hasOpenedList, setHasOpenedList] = useState(false);
  // The dropdown is controlled so a paste can close it. Clicking the field to
  // paste opens the list, which then covers the Go button — the user had to
  // click away to dismiss it before they could submit.
  const [listOpen, setListOpen] = useState(false);

  const { options, isLoading, wellNamesFailed } = useFilterWellOptions(
    format,
    hasOpenedList,
    isOpen
  );

  // antd filters in place on every keystroke — an O(n) scan over the whole
  // filter. Debounced and capped, a 50k-well filter costs one scan per pause
  // rather than one per character. The cap bounds rendering only: submitting is
  // driven by `selected`, which a paste can fill with never-rendered entries.
  const [search, setSearch] = useState("");
  const commitSearch = useDebouncedCallback(setSearch, SEARCH_DEBOUNCE_MS);

  const visibleOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = needle
      ? options.filter((option) => option.label.toLowerCase().includes(needle))
      : options;
    return matches.length > MAX_RENDERED_OPTIONS
      ? matches.slice(0, MAX_RENDERED_OPTIONS)
      : matches;
  }, [options, search]);

  const submitText = useMemo(() => selected.join("\n"), [selected]);

  const canSubmit = status !== "busy" && selected.length > 0;
  // Counted off the parsed submission, not `selected`: collectWellEntries
  // dedupes case-insensitively, so two spellings of one name could push
  // `selected` past the cap while nothing was truncated — a false notice that
  // also held the panel open.
  const truncated = useMemo(
    () => collectWellEntries(submitText).length > JUMP_MAX_HIGHLIGHT_WELLS,
    [submitText]
  );

  useEffect(() => {
    if (!isOpen) {
      cancelPending();
      // Also reset the list. Closing the panel does not fire
      // onDropdownVisibleChange, and the Select is remounted on `isOpen`, so a
      // submit that closed the panel from the keyboard (Ctrl+Enter, which
      // never blurs) left listOpen true — and the Select then mounted with the
      // list already open over Go, which is the state this PR exists to fix.
      // Handled here rather than at the submit, so every close route is
      // covered.
      setListOpen(false);
    }
    // cancelPending is redefined each render but closes over refs and stable
    // setters only, so a stale reference behaves identically.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function handleSelectChange(value: string[]) {
    setSelected(value);
    resetFeedback();
  }

  /**
   * Pasting a well list adds every entry at once. Handled here rather than by
   * antd's `tokenSeparators`, which splits the raw text and stops:
   * collectWellEntries also unformats the entries that are genuine UWIs while
   * leaving names alone (EVA-6890).
   */
  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    const pasted = event.clipboardData?.getData("text") ?? "";
    const entries = collectWellEntries(pasted);
    if (entries.length === 0) return;

    event.preventDefault();
    resetFeedback();
    setSelected((current) => Array.from(new Set([...current, ...entries])));
    // A paste is a complete answer, so get out of the way: there is nothing
    // left to pick from the list, and it sits over the Go button.
    setListOpen(false);
  }

  function handleClear() {
    setSelected([]);
    // Also invalidates any in-flight submit: a Paste → Go → Clear all sequence
    // must not let the stale response re-apply the highlight just cleared.
    clearJump();
  }

  async function handleSubmit() {
    if (!canSubmit) return;

    const fullMatch = await submitJump(submitText);

    // `truncated` holds the panel open: a 6000-entry paste sends only the first
    // 5000, and closing would hide the cap notice.
    if (fullMatch && !truncated) {
      closeMenu();
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Ctrl+Enter submits. Plain Enter belongs to the combobox — it commits the
    // entry being typed — so it must not reach the jump.
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  }

  return (
    <WellJumpContainer>
      <Select<WellJumpFormat>
        id="jump-well-format"
        data-testid="jump-well-format"
        aria-label="Identifier format"
        className="well-jump-field"
        value={format}
        onChange={setFormat}
        options={FORMAT_OPTIONS}
      />

      <div className="well-jump-field" onPaste={handlePaste} onKeyDown={handleKeyDown}>
        <Select<string[]>
          // Remounted when the panel opens: maxTagCount="responsive" measures the
          // selector to decide how many tags fit, and the popover's content
          // mounts before layout, so the first measurement runs against a
          // zero-width row and collapses everything to "+N".
          key={isOpen ? "open" : "closed"}
          mode="tags"
          allowClear
          className="well-jump-field"
          data-testid="jump-well-select"
          aria-label="Select or paste wells"
          placeholder={isLoading ? "Loading wells…" : "Select wells, or paste a list"}
          loading={isLoading}
          value={selected}
          onChange={handleSelectChange}
          options={visibleOptions}
          onSearch={commitSearch}
          // Gated on isOpen too: a closed panel has no options, and the effect
          // above resets listOpen only after the render that closes it, so the
          // list would flash "No data" once on the way out.
          open={isOpen && listOpen}
          onDropdownVisibleChange={(open) => {
            setListOpen(open);
            if (open) setHasOpenedList(true);
          }}
          // Filtering is done above, once per debounced search, instead of by
          // antd on every keystroke over the full list.
          filterOption={false}
          // "responsive" measures the row, so antd renders only the visible tags
          // plus the overflow chip — a 50k-well paste costs a handful of nodes.
          maxTagCount="responsive"
          maxTagPlaceholder={(omitted) => (
            <span data-testid="jump-well-summary">{`+${omitted.length.toLocaleString()}`}</span>
          )}
          // Newline/CR/tab only, matching collectWellEntries — never comma or
          // space. Covers typing; handlePaste covers pasting.
          tokenSeparators={["\n", "\r", "\t"]}
        />
      </div>

      <JumpActionRow
        idPrefix="jump-well"
        canSubmit={canSubmit}
        onClear={handleClear}
        onSubmit={handleSubmit}
      />
      {status === "busy" && (
        <div className="flex-row justify-center">Looking up wells…</div>
      )}
      {matched && matched.matched !== matched.requested && (
        <div className="flex-row justify-center">{formatMatchedCount(matched)}</div>
      )}
      {truncated && <div className="flex-row justify-center">{ENTRY_CAP_MESSAGE}</div>}
      {error && (
        <div className="flex-row justify-center">
          <FieldErrorMessage message={error} />
        </div>
      )}
      {wellNamesFailed && (
        <div className="flex-row justify-center">
          <FieldErrorMessage message={WELL_NAMES_FAILED_MESSAGE} />
        </div>
      )}
    </WellJumpContainer>
  );
}

const WellJumpContainer = styled.div`
  ${jumpPanelStyles}

  /* The format picker and the well selector are one stacked pair, so they share
     a width rather than one sitting inset beside a label. */
  .well-jump-field {
    width: 100%;
  }

  /* The tag row is one line, always: a fixed maxTagCount wraps as soon as the
     tags outgrow the row, and a mis-measured responsive row could too. Clamped
     here so neither can — the overflow chip absorbs the rest. */
  .well-jump-field .ant-select-selector {
    flex-wrap: nowrap;
    overflow: hidden;
  }

  .well-jump-field .ant-select-selection-overflow {
    flex-wrap: nowrap;
  }
`;
